import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useWebSocket } from '../../../hooks/useWebSocket.hook';
import { buildWorkSessionQueryKey } from '../lib/query-keys';

interface ProcessedEvent {
	type: string;
	correlationId?: string;
	timestamp: number;
}

const EVENT_DEDUP_WINDOW_MS = 5000;
const MAX_PROCESSED_EVENTS = 100;

export function useWorkSessionSocket() {
	const { subscribe, isConnected } = useWebSocket();
	const queryClient = useQueryClient();
	const processedEventsRef = useRef<ProcessedEvent[]>([]);
	const qk = buildWorkSessionQueryKey();

	const isDuplicate = (eventType: string, correlationId?: string): boolean => {
		if (!correlationId) {
			return false;
		}

		const now = Date.now();
		processedEventsRef.current = processedEventsRef.current.filter(e => now - e.timestamp < EVENT_DEDUP_WINDOW_MS);

		const exists = processedEventsRef.current.some(e => e.type === eventType && e.correlationId === correlationId);

		if (!exists) {
			processedEventsRef.current.push({ type: eventType, correlationId, timestamp: now });

			if (processedEventsRef.current.length > MAX_PROCESSED_EVENTS) {
				processedEventsRef.current.shift();
			}
		}

		return exists;
	};

	useEffect(() => {
		if (!isConnected) {
			return;
		}

		const unsubscribePropose = subscribe<{ workSession: unknown; correlationId?: string }>('newWorkSessionPropose', data => {
			if (isDuplicate('newWorkSessionPropose', data.correlationId)) {
				console.log('[WorkSessionSocket] Duplicate newWorkSessionPropose event, skipping');
				return;
			}

			console.log('[WorkSessionSocket] New work session proposed:', data);
			queryClient.invalidateQueries({ queryKey: qk.proposed });
			queryClient.invalidateQueries({ queryKey: qk.active });
		});

		const unsubscribeStepUpdate = subscribe<{ step: { id: number; stepType: string; sequence: number }; correlationId?: string }>('workSessionStepUpdate', async data => {
			if (isDuplicate('workSessionStepUpdate', data.correlationId)) {
				console.log('[WorkSessionSocket] Duplicate workSessionStepUpdate event, skipping');
				return;
			}

			console.log('[WorkSessionSocket] Work session step update:', data);

			const currentStep = queryClient.getQueryData<{ stepType: string; sequence: number }>(qk.step);

			if (currentStep && data.step.sequence < currentStep.sequence) {
				console.warn('[WorkSessionSocket] Received older step, refetching to reconcile');
				await queryClient.refetchQueries({ queryKey: qk.active });
				await queryClient.refetchQueries({ queryKey: qk.step });
				return;
			}

			queryClient.invalidateQueries({ queryKey: qk.active });
			queryClient.invalidateQueries({ queryKey: qk.step });
		});

		const unsubscribeCancelled = subscribe<{ reason: string; correlationId?: string }>('workSessionCancelled', data => {
			if (isDuplicate('workSessionCancelled', data.correlationId)) {
				console.log('[WorkSessionSocket] Duplicate workSessionCancelled event, skipping');
				return;
			}

			console.log('[WorkSessionSocket] Work session cancelled:', data);
			queryClient.invalidateQueries({ queryKey: qk.proposed });
			queryClient.invalidateQueries({ queryKey: qk.active });
		});

		const unsubscribeCompleted = subscribe<{ sessionId: number; correlationId?: string }>('workSessionCompleted', data => {
			if (isDuplicate('workSessionCompleted', data.correlationId)) {
				console.log('[WorkSessionSocket] Duplicate workSessionCompleted event, skipping');
				return;
			}

			console.log('[WorkSessionSocket] Work session completed:', data);
			queryClient.invalidateQueries({ queryKey: qk.active });
		});

		return () => {
			unsubscribePropose();
			unsubscribeStepUpdate();
			unsubscribeCancelled();
			unsubscribeCompleted();
		};
	}, [subscribe, isConnected, queryClient, qk.active, qk.proposed, qk.step]);

	useEffect(() => {
		if (isConnected) {
			console.log('[WorkSessionSocket] WebSocket connected, refetching queries');
			queryClient.invalidateQueries({ queryKey: qk.all });
		}
	}, [isConnected, queryClient, qk.all]);

	return { isConnected };
}
